import { useEffect, useRef, useState } from "preact/hooks";
import type { DisplayState } from "../state.js";

const IMMEDIATE: ReadonlySet<DisplayState> = new Set(["connecting", "speaking", "error"]);
const DEBOUNCE_MS = 500;

export interface StatusLabelProps {
  displayState: DisplayState;
  strings: Record<DisplayState, string>;
}

export function StatusLabel({ displayState, strings }: StatusLabelProps) {
  const [displayedState, setDisplayedState] = useState(displayState);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (IMMEDIATE.has(displayState)) {
      setDisplayedState(displayState);
      return;
    }
    timer.current = setTimeout(() => setDisplayedState(displayState), DEBOUNCE_MS);
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [displayState]);

  // Shimmer only while the agent is working/waiting; other states are static.
  const shimmer = displayState === "connecting" || displayState === "thinking";
  return (
    <span
      class={shimmer ? "va-status-label va-shimmer" : "va-status-label"}
      data-testid="va-status-label"
      role="status"
      aria-live="polite"
    >
      {strings[displayedState]}
    </span>
  );
}
